import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Seo } from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Store } from "lucide-react";

export default function RestaurantAuth() {
  const navigate = useNavigate();
  const { loginOwner } = useAuth();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", restaurant_name: "", cuisine: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const res = await api.post("/auth/restaurant/register", {
          email: form.email, password: form.password,
          restaurant_name: form.restaurant_name, cuisine: form.cuisine || "Autre",
        });
        loginOwner(res.data.access_token, res.data.owner);
        toast.success(t("toast.restaurantCreated"));
      } else {
        const res = await api.post("/auth/restaurant/login", { email: form.email, password: form.password });
        loginOwner(res.data.access_token, res.data.owner);
        toast.success(t("toast.loginSuccess"));
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toast.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Espace Restaurant — Vytali"
        description="Accédez à l'espace restaurateur Vytali pour gérer votre fiche et votre menu."
        path="/restaurant"
        noIndex
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8">
          <span className="w-12 h-12 rounded-full bg-[#2B2B2B] flex items-center justify-center mb-6">
            <Store className="w-6 h-6 text-white" />
          </span>
          <h1 className="text-3xl font-display font-black tracking-tighter text-[#2B2B2B]">
            {mode === "login" ? t("rauth.loginTitle") : t("rauth.registerTitle")}
          </h1>
          <p className="mt-2 text-[#5C5C5C]">
            {mode === "login" ? t("rauth.subtitleLogin") : t("rauth.subtitleRegister")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <input data-testid="reg-restaurant-name" required value={form.restaurant_name} onChange={set("restaurant_name")} placeholder={t("rauth.restaurantName")}
                  className="w-full h-12 px-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors" />
                <input data-testid="reg-cuisine" value={form.cuisine} onChange={set("cuisine")} placeholder={t("rauth.cuisine")}
                  className="w-full h-12 px-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors" />
              </>
            )}
            <input data-testid="auth-email" type="email" required value={form.email} onChange={set("email")} placeholder={t("clogin.email")}
              className="w-full h-12 px-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors" />
            <input data-testid="auth-password" type="password" required value={form.password} onChange={set("password")} placeholder={t("clogin.password")}
              className="w-full h-12 px-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors" />

            <button data-testid="auth-submit-btn" type="submit" disabled={busy}
              className="w-full h-12 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50">
              {busy ? "…" : mode === "login" ? t("rauth.submitLogin") : t("rauth.submitRegister")}
            </button>

            {mode === "login" && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-[#5C5C5C] hover:text-[#E63946] hover:underline"
                >
                  {t("rauth.forgotPassword")}
                </button>
              </div>
            )}
          </form>

          <p className="mt-6 text-center text-sm text-[#5C5C5C]">
            {mode === "login" ? t("rauth.noAccount") : t("rauth.haveAccount")}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-bold text-[#E63946] hover:underline">
              {mode === "login" ? t("rauth.createAccount") : t("rauth.submitLogin")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
